import { useRef } from "react";
import {
  Box,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import {
  currencies,
  customersType,
  billingOptions,
} from "src/components/apps/variables";
import { createCustomer } from "src/queries/queriess";
import { createOrder } from "src/queries/query";
import { useNavigate } from "react-router";
import { useQuote } from "src/contexts/QuoteContext";

const AddCustomer = ({ onClose, createFromQuote = false }) => {
  const formikRef = useRef(null);
  const navigate = useNavigate();
  const { quoteData, setCustomerData } = useQuote();
  const Quotes = [
    { label: "Quote Reuqired", value: true },
    { label: " NO Quote Reuqired", value: false },
  ];

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = {
      name: values.name,
      shortCode: values.shortCode,
      addresses: {
        address: values.address1,
        address2: values.address2 || "",
        city: values.city,
        state: values.state,
        postal: values.postal,
        country: values.country,
        latitude: values.latitude || 0,
        longitude: values.longitude || 0,
      },
      customerType: values.customerType,
      currency: values.currency,
      requireQuote: values.requireQuote,
      billingOption: values.billingOption,
      isActive: values.isActive,
    };

    try {
      const response = await createCustomer(payload);
      toast.success("Customer created successfully!");
      resetForm();
      // Handle success case
      if (response.status === 200) {
        // Save customer data to context
        setCustomerData(payload);

        if (createFromQuote) {
          const data = {
            billingParty: payload,
            // Include quote data if it exists
            ...(quoteData && { quote: quoteData }),
          };
          const result = await createOrder(data);
          if (result?.status === 200) {
            toast.success("Order created successfully.");
            navigate(`/order/new/${result?.data?.data?.uuid}`);
          } else {
            toast.error("Cannot create order.");
          }
        }
      }
      onClose && onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create customer");
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    shortCode: Yup.string().required("Short code is required"),
    customerType: Yup.string().required("Customer type is required"),
    billingOption: Yup.string().required("Billing option is required"),
    requireQuote: Yup.boolean().required("Quote requirement is required"),
    currency: Yup.string().required("Currency is required"),
    isActive: Yup.boolean().required("Active status is required"),
  });

  return (
    <Box
      sx={{
        width: "100%",
        padding: 0,
      }}
    >
      <Formik
        innerRef={formikRef}
        initialValues={{
          name: "",
          shortCode: "",
          address1: "",
          address2: "",
          city: "",
          state: "",
          longitude: "",
          latitude: "",
          postal: "",
          country: "",
          customerType: "",
          billingOption: "",
          isActive: true,
          requireQuote: true,
          currency: "CAD",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          touched,
          errors,
        }) => (
          <Form id="customer-add-form" noValidate>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Field
                  name="name"
                  as={TextField}
                  label="Organization Name"
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  helperText={<ErrorMessage name="name" />}
                  error={Boolean(touched.name && errors.name)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  name="shortCode"
                  as={TextField}
                  label="Short Code"
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.shortCode}
                  helperText={<ErrorMessage name="shortCode" />}
                  error={Boolean(touched.shortCode && errors.shortCode)}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Typography variant="caption" sx={{ mb: 1 }}>
                  Address Auto Complete
                </Typography>
                <GooglePlacesAutocomplete
                  variant="outlined"
                  apiKey={import.meta.env.VITE_API_MAP_KEY}
                  selectProps={{
                    value: values.address1
                      ? { label: values.address1, value: values.address1 }
                      : null,
                    onChange: (place) => {
                      // Set the address1 field
                      setFieldValue("address1", place.label);
                      // Fetch place details to get coordinates and address components
                      const placeId = place.value.place_id;
                      const geocoder = new window.google.maps.Geocoder();
                      geocoder.geocode(
                        { placeId: placeId },
                        (results, status) => {
                          if (status === "OK" && results[0]) {
                            const place = results[0];
                            // Set coordinates
                            const lat = place.geometry.location.lat();
                            const lng = place.geometry.location.lng();
                            setFieldValue("latitude", lat);
                            setFieldValue("longitude", lng);
                            // Extract address components
                            let city = "";
                            let state = "";
                            let country = "";
                            let postalCode = "";
                            let county = "";
                            place.address_components.forEach((component) => {
                              const types = component.types;
                              if (types.includes("locality")) {
                                city = component.long_name;
                              } else if (
                                types.includes("administrative_area_level_1")
                              ) {
                                state = component.long_name;
                              } else if (types.includes("country")) {
                                country = component.long_name;
                              } else if (types.includes("postal_code")) {
                                postalCode = component.long_name;
                              } else if (
                                types.includes("administrative_area_level_2")
                              ) {
                                county = component.long_name;
                              }
                            });
                            // Set the extracted values
                            setFieldValue("city", city);
                            setFieldValue("state", state);
                            setFieldValue("country", country);
                            setFieldValue("postal", postalCode);
                            // If you have a county field, uncomment the line below
                            // setFieldValue('county', county);
                          }
                        },
                      );
                    },
                    placeholder: "Enter address",
                    styles: {
                      control: (provided) => ({
                        ...provided,
                        height: 35,
                        minHeight: 35,
                        borderRadius: 8,
                        zIndex: 99999999999999999999,
                      }),

                      option: (provided) => ({
                        ...provided,

                        borderRadius: 8,
                        zIndex: 99999999999999999999,
                      }),
                      menuList: (provided) => ({
                        ...provided,

                        borderRadius: 8,
                        zIndex: 99999999999999999999,
                      }),
                      menu: (provided) => ({
                        ...provided,

                        borderRadius: 8,
                        zIndex: 99999999999999999999,
                      }),
                    },
                  }}
                />
                {touched.address1 && errors.address1 && (
                  <FormHelperText error>{errors.address1}</FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  name="address2"
                  as={TextField}
                  label="Address 2"
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  value={values.address2}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={<ErrorMessage name="address2" />}
                  error={Boolean(touched.address2 && errors.address2)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  name="city"
                  as={TextField}
                  label="City"
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  value={values.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={<ErrorMessage name="city" />}
                  error={Boolean(touched.city && errors.city)}
                  inputProps={{ autoComplete: "off" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  name="state"
                  as={TextField}
                  label="State"
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  value={values.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={<ErrorMessage name="state" />}
                  error={Boolean(touched.state && errors.state)}
                  inputProps={{ autoComplete: "off" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  name="postal"
                  as={TextField}
                  label="Postal Code"
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  value={values.postal}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={<ErrorMessage name="postal" />}
                  error={Boolean(touched.postal && errors.postal)}
                  inputProps={{ autoComplete: "off" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  name="country"
                  as={TextField}
                  label="Country"
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  value={values.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={<ErrorMessage name="country" />}
                  error={Boolean(touched.country && errors.country)}
                  inputProps={{ autoComplete: "off" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field name="customerType">
                  {({ field, form }) => (
                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={Boolean(
                        form.errors.customerType && form.touched.customerType,
                      )}
                    >
                      <InputLabel id="type-label" shrink={true}>
                        Customer Type
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="type-label"
                        label="Customer Type"
                        variant="outlined"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.customerType}
                      >
                        {customersType?.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {form.errors.customerType && form.touched.customerType
                          ? form.errors.customerType
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field name="currency">
                  {({ field, form }) => (
                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={Boolean(
                        form.errors.currency && form.touched.currency,
                      )}
                    >
                      <InputLabel id="currency-label" shrink={true}>
                        Currency
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="currency-label"
                        label="Currency"
                        variant="outlined"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.currency}
                      >
                        {currencies?.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {form.errors.currency && form.touched.currency
                          ? form.errors.currency
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field name="requireQuote">
                  {({ field, form }) => (
                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={Boolean(
                        form.errors.requireQuote && form.touched.requireQuote,
                      )}
                    >
                      <InputLabel id="requireQuote-label" shrink={true}>
                        Default Quote Required
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="requireQuote-label"
                        label="Default Quote Required"
                        variant="outlined"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.requireQuote}
                      >
                        {Quotes?.map((quote) => (
                          <MenuItem key={quote?.label} value={quote?.value}>
                            {quote.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {form.errors.requireQuote && form.touched.requireQuote
                          ? form.errors.requireQuote
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field name="billingOption">
                  {({ field, form }) => (
                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={Boolean(
                        form.errors.billingOption && form.touched.billingOption,
                      )}
                    >
                      <InputLabel id="billingOption-label" shrink={true}>
                        Default Billing Option
                      </InputLabel>
                      <Select
                        labelId="billingOption-label"
                        id="billingOption"
                        {...field}
                        label="Default Billing Option"
                        variant="outlined"
                      >
                        {billingOptions?.map((billing) => (
                          <MenuItem key={billing} value={billing}>
                            {billing}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {form.touched.billingOption && form.errors.billingOption
                          ? form.errors.billingOption
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field name="isActive">
                  {({ field, form }) => (
                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={Boolean(
                        form.errors.isActive && form.touched.isActive,
                      )}
                    >
                      <InputLabel id="isActive-label" shrink={true}>
                        Active Status
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="isActive-label"
                        label="Active Status"
                        variant="outlined"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.isActive}
                      >
                        <MenuItem value={true}>Active</MenuItem>
                        <MenuItem value={false}>Inactive</MenuItem>
                      </Select>
                      <FormHelperText>
                        {form.errors.isActive && form.touched.isActive
                          ? form.errors.isActive
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  )}
                </Field>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default AddCustomer;
